import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import t from 'utils/translate';
import {
    execContactsClientAction,
    updateContactsSelectorData,
    getContactsSelectorData,
    getOrLoadGroupDetails,
    groupHasTransmissionDenied,
    getFavoriteContacts,
    openPbxDialer,
} from '../stores/contacts-slice-handler';
import { selectDataLoaded } from '../../settings/stores/company-settings-slice-handler';
import { Markup } from 'interweave';
import { createEmptyContactsIdsList, getAllContactsMenusOptionsOrdered } from '../../../utils/contact-util';
import { ContactMenuOption, MixedActionMode } from '../../../constants/contacts-constants';
import { arrayDiff } from '../../../utils/functions';
import Button from '@mui/material/Button';
import ContactUserItem from './contact-user-item';
import ContactGroupItem from './contact-group-item';
import TextInput from '../../shared/components/text-input';
import LabelWithCheckbox from '../../shared/components/label-with-checkbox';
import Preloader from '../../shared/components/preloader';
import { ScrollToTarget } from '../../shared/components/scroll-to-target';

/**
 * This component works in conjunction with a Contacts Selector component and is used to
 * execute action with the selected contacts.
 * @param {string} contactsSelectorUuid The id of the source contacts selector.
 * @param {ContactsSelectorClientOptions} options
 * @return {JSX.Element}
 * @constructor
 */
export default function ContactsSelectorClient({ contactsSelectorUuid, options }) {
    /**
     * @type {ContactsSelectorData}
     */
    const contactsSelectorData = useSelector(() => getContactsSelectorData(contactsSelectorUuid));
    const companySettingsLoaded = useSelector(selectDataLoaded);
    const selectedContactsCount = contactsSelectorData
        ? contactsSelectorData.msisdns.length + contactsSelectorData.groupsIds.length
        : 0;
    const [groupConversationName, setGroupConversationName] = useState('');
    const [mixedActionGroupNameOn, setMixedActionGroupNameOn] = useState(false);
    const [availableOnlyReception, setAvailableOnlyReception] = useState(false);
    const featuresWithAvailabilityRestrictions = [ContactMenuOption.BROADCAST_CALL];

    /**
     * @type {ContactsSelectorData}
     */
    const [favoriteContactsData, setFavoritesContactsData] = useState(createEmptyContactsIdsList());
    const [favoriteContactsLoading, setFavoritesContactsLoading] = useState(false);

    useEffect(() => {
        if (options.action === ContactMenuOption.ADD_TO_FAVORITES) {
            setFavoritesContactsLoading(true);
            getFavoriteContacts().then((favoriteContacts) => {
                setFavoritesContactsData(favoriteContacts);
                setFavoritesContactsLoading(false);
                updateContactsSelectorData(contactsSelectorUuid, favoriteContacts);
            });
        }
    }, [options.action]);

    const canHaveAvailableOnlyReceptionControls = () => {
        if (contactsSelectorData.msisdns.length > 0 || contactsSelectorData.groupsIds.length !== 1) {
            return false;
        }

        const group = getOrLoadGroupDetails(contactsSelectorData.groupsIds[0]);

        if (group === null) {
            return false;
        }

        return group.AvailableOnlyReception === true
        && !featuresWithAvailabilityRestrictions.includes(options.action)
        && !groupHasTransmissionDenied(group.GroupId);
    };

    const renderGroupConversationNameInput = () => (
        <TextInput
            value={groupConversationName}
            onChange={(e) => setGroupConversationName(e.target.value)}
            maxLength={100}
            placeholder={t('js_all_group_name_placeholder')}
            data-test-id="group-conversation-name-input"
        ></TextInput>
    );

    const renderActionButton = () => {
        const mixedActions = [ContactMenuOption.MIXED_SHARE, ContactMenuOption.MIXED_FORWARD];

        if (mixedActions.includes(options.action)) {
            return renderMixedActionButtons();
        }

        return renderSingleActionButton();
    };

    const renderSingleActionButton = () => {
        // determine the disable state
        let buttonDisabled = false;

        do {
            if (contactsSelectorData === null || selectedContactsCount === 0) {
                buttonDisabled = true;
                break;
            }

            if (options.action === ContactMenuOption.GROUP_CONVERSATION && groupConversationName.trim().length === 0) {
                buttonDisabled = true;
                break;
            }

            if (options.action === ContactMenuOption.TRANSFER_CALL) {
                // there must be one user only
                if (contactsSelectorData.msisdns.length !== 1 && contactsSelectorData.groupsIds.length !== 0) {
                    buttonDisabled = true;
                }

                break;
            }

            if (options.action === ContactMenuOption.REACH_BACK_REQUEST) {
                // there must be one user only
                if (contactsSelectorData.msisdns.length === 0 && contactsSelectorData.groupsIds.length !== 0) {
                    buttonDisabled = true;
                }

                break;
            }

            if (
                [
                    ContactMenuOption.ADD_CALL_PARTICIPANTS,
                    ContactMenuOption.PTT_CALL,
                    ContactMenuOption.SHARE_SCREEN,
                    ContactMenuOption.BROADCAST_VIDEO,
                ].includes(options.action)
            ) {
                // there must be one at least one contact
                if (contactsSelectorData.msisdns.length === 0 && contactsSelectorData.groupsIds.length === 0) {
                    buttonDisabled = true;
                }

                break;
            }

            if (options.action !== ContactMenuOption.ADD_TO_FAVORITES) {
                // there must be at least two users or a group for multiple select
                if (contactsSelectorData.msisdns.length === 1 && contactsSelectorData.groupsIds.length == 0) {
                    buttonDisabled = true;
                }

                break;
            }

            if (
                arrayDiff(contactsSelectorData.msisdns, favoriteContactsData.msisdns).length === 0 &&
                arrayDiff(contactsSelectorData.groupsIds, favoriteContactsData.groupsIds).length === 0
            ) {
                buttonDisabled = true;
            }
        } while (false);

        // determine the label of the button
        /**
         * @type {MenuOptionData[]}
         */
        const allMenuOptions = getAllContactsMenusOptionsOrdered();
        const menuOption = allMenuOptions.filter((menuOption) => menuOption.feature === options.action)[0];

        return (
            <>
                <Button
                    key={`${options.action}-${contactsSelectorUuid}`}
                    variant="contained"
                    role="primary"
                    disabled={buttonDisabled}
                    data-test-id={`${options.action}-button`}
                    onClick={() => {
                        let additionalEventParams = { ...(options.additionalParams || {}) };

                        if (options.action === ContactMenuOption.GROUP_CONVERSATION) {
                            additionalEventParams = { ...additionalEventParams, groupConversationName };
                        }

                        if (canHaveAvailableOnlyReceptionControls()) {
                            additionalEventParams.availableOnlyReception = availableOnlyReception;
                        }

                        execContactsClientAction(contactsSelectorUuid, options.action, additionalEventParams);

                        if (options.postActionCallback) {
                            setTimeout(() => options.postActionCallback());
                        }
                    }}
                >
                    {menuOption.text}
                </Button>
            </>
        );
    };

    const renderMixedActionButtons = () => {
        if (selectedContactsCount == 0) {
            return '';
        }

        let additionalEventParams = { ...(options.additionalParams || {}) };

        // just one button for a single recipient
        if (selectedContactsCount == 1) {
            let actionButtonLabel = '';
            switch (options.action) {
                case ContactMenuOption.MIXED_SHARE:
                    actionButtonLabel = t('js_all_meeting_rooms_share_single');
                    break;

                case ContactMenuOption.MIXED_FORWARD:
                    actionButtonLabel = t('js_all_message_forward');
                    break;
            }

            return (
                <Button
                    variant="contained"
                    role="primary"
                    data-test-id="contacts-selector-favorites-button"
                    onClick={() => {
                        additionalEventParams.actionMode = MixedActionMode.SINGLE;
                        execContactsClientAction(contactsSelectorUuid, options.action, additionalEventParams);

                        if (options.postActionCallback) {
                            setTimeout(() => options.postActionCallback());
                        }
                    }}
                >
                    {actionButtonLabel}
                </Button>
            );
        }

        // initial state, with two buttons when there are more than one contact selected
        if (mixedActionGroupNameOn === false) {
            let broadcastButtonLabel = '';
            let groupButtonLabel = '';

            switch (options.action) {
                case ContactMenuOption.MIXED_SHARE:
                    broadcastButtonLabel = t('meeting_rooms_conversations_share_separately_cta');
                    groupButtonLabel = t('meeting_rooms_conversations_share_create_cta');
                    break;

                case ContactMenuOption.MIXED_FORWARD:
                    broadcastButtonLabel = t('js_all_message_forward_multiple_separately');
                    groupButtonLabel = t('js_all_message_forward_multiple_group');
                    break;
            }

            return (
                <>
                    <Button
                        variant="contained"
                        role="primary"
                        data-test-id="broadcast-button"
                        onClick={() => {
                            additionalEventParams.actionMode = MixedActionMode.SEPARATE;
                            execContactsClientAction(contactsSelectorUuid, options.action, additionalEventParams);

                            if (options.postActionCallback) {
                                setTimeout(() => options.postActionCallback());
                            }
                        }}
                    >
                        {broadcastButtonLabel}
                    </Button>
                    <Button
                        variant="contained"
                        role="primary"
                        data-test-id="group-opener-button"
                        onClick={() => {
                            setMixedActionGroupNameOn(true);
                        }}
                    >
                        {groupButtonLabel}
                    </Button>
                </>
            );
        }

        let actionButtonLabel = '';
        switch (options.action) {
            case ContactMenuOption.MIXED_SHARE:
                actionButtonLabel = t('js_all_meeting_rooms_share_single');
                break;

            case ContactMenuOption.MIXED_FORWARD:
                actionButtonLabel = t('js_all_message_forward');
                break;
        }

        // the controls for the group conversation and final step in share are ON
        return (
            <>
                <Button
                    variant="contained"
                    role="primary"
                    className={'back-button'}
                    onClick={() => {
                        setMixedActionGroupNameOn(false);
                    }}
                >
                    ‹
                </Button>
                {renderGroupConversationNameInput()}
                <Button
                    variant="contained"
                    role="primary"
                    disabled={groupConversationName.length == 0}
                    onClick={() => {
                        additionalEventParams.groupConversationName = groupConversationName;
                        additionalEventParams.actionMode = MixedActionMode.GROUP;
                        execContactsClientAction(contactsSelectorUuid, options.action, additionalEventParams);

                        if (options.postActionCallback) {
                            setTimeout(() => options.postActionCallback());
                        }
                    }}
                >
                    {actionButtonLabel}
                </Button>
            </>
        );
    };

    const addUnselectButton = (msisdn, groupId) => {
        return (
            <div
                className="unselect-button"
                data-test-id={`unselect-button-${msisdn}`}
                onClick={() => {
                    const newContactsSelectorData = {
                        msisdns: [...contactsSelectorData.msisdns],
                        groupsIds: [...contactsSelectorData.groupsIds],
                    };

                    if (msisdn !== null) {
                        // will unselect this user
                        newContactsSelectorData.msisdns = contactsSelectorData.msisdns.filter(
                            (selectedMsisdn) => selectedMsisdn !== msisdn,
                        );
                    }

                    if (groupId !== null) {
                        // will unselect this group
                        newContactsSelectorData.groupsIds = contactsSelectorData.groupsIds.filter(
                            (selectedGroupId) => selectedGroupId !== groupId,
                        );
                    }

                    updateContactsSelectorData(contactsSelectorUuid, newContactsSelectorData);
                }}
            >
                <span className="stw-more-actions"></span>
            </div>
        );
    };

    const renderContent = () => {
        if (!companySettingsLoaded || !contactsSelectorData) {
            return '';
        }

        const bothUsersAnGroupsAreSelected =
            contactsSelectorData.msisdns.length > 0 && contactsSelectorData.groupsIds.length > 0;

        return (
            <div className="contacts-selector-client" data-selector-id={contactsSelectorUuid}>
                <div className="selection-bar">
                    <div
                        className="available-only-reception-container"
                        data-test-id="available-only-reception-checkbox"
                    >
                        {canHaveAvailableOnlyReceptionControls() && (
                            <LabelWithCheckbox
                                labelContent={t('js_all_popup_walkie_talkie_available_short')}
                                onChange={(e) => {
                                    setAvailableOnlyReception(e.target.checked);
                                }}
                                checked={availableOnlyReception}
                            ></LabelWithCheckbox>
                        )}
                    </div>
                    <div className="controls-container">
                        {options.action === ContactMenuOption.GROUP_CONVERSATION && renderGroupConversationNameInput()}
                        {renderActionButton()}
                        <Button
                            className="no-button"
                            data-test-id="contacts-selector-cancel-button"
                            variant="contained"
                            role="cancel"
                            onClick={() => options.cancelCallback()}
                        >
                            {t('js_all_cancel')}
                        </Button>
                    </div>
                </div>
                {favoriteContactsLoading && <Preloader asOverlay />}
                <div className="selected-entities-container">
                    <div className="counter-container">
                        <Markup content={t('js_all_contacts_selected').replace('%n%', selectedContactsCount)} />
                    </div>
                    <div className="full-area">
                        {contactsSelectorData.msisdns.length > 0 && (
                            <div className={`half-area users ${bothUsersAnGroupsAreSelected ? '' : 'single'}`}>
                                <div className="title-bar">{t('js_all_users')}</div>
                                <div className="entities-container">
                                    {contactsSelectorData.msisdns.map((msisdn) => (
                                        <ContactUserItem key={`selected-user-${msisdn}`} msisdn={msisdn}>
                                            {addUnselectButton(msisdn, null)}
                                        </ContactUserItem>
                                    ))}
                                    <ScrollToTarget />
                                </div>
                            </div>
                        )}
                        {contactsSelectorData.groupsIds.length > 0 && (
                            <div className={`half-area groups ${bothUsersAnGroupsAreSelected ? '' : 'single'}`}>
                                <div className="title-bar">{t('js_all_groups')}</div>
                                <div className="entities-container">
                                    {contactsSelectorData.groupsIds.map((groupId) => (
                                        <ContactGroupItem key={`selected-group-${groupId}`} groupId={groupId}>
                                            {addUnselectButton(null, groupId)}
                                        </ContactGroupItem>
                                    ))}
                                    <ScrollToTarget />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return renderContent();
}
